/**
 * PLANCHES A4 D'ÉTIQUETTES — module PUR (aucun import d'exécution).
 * ───────────────────────────────────────────────────────────────────────────
 *
 *  1. la MISE EN PAGE : où tombe chaque étiquette sur une planche autocollante,
 *     avec la possibilité de COMMENCER À UNE CASE DONNÉE — une planche entamée
 *     se termine, elle ne se jette pas ;
 *  2. les motifs Code 128, éprouvés par `backend/tests/etiquettes-planche.test.js`.
 *
 * ⚠️ LA PLANCHE PORTE UN DATAMATRIX, PAS UN CODE 128 (RQ, 11/10/2026 : même
 * étiquette que la Zebra). Le dessin est dans `datamatrix.ts` ; les motifs
 * Code 128 ci-dessous ne servent plus à `planchePdf` et sont gardés pour leur
 * test.
 */

export interface GrillePlanche {
  lignes: number; colonnes: number;
  margeHautMm: number; margeGaucheMm: number;
  largeurMm: number; hauteurMm: number;
  pasHorizontalMm: number; pasVerticalMm: number;
  verifieLe?: string;
}

export interface CasePlanche {
  /** Rang de l'étiquette dans la liste demandée (0…n-1). */
  etiquette: number;
  /** Case sur la planche, numérotée de 1, ligne par ligne. */
  numeroCase: number;
  ligne: number; colonne: number;
  xMm: number; yMm: number;
}

/** Répartit `n` étiquettes sur des planches, la première commençant à la case
 *  `caseDepart` (1 = en haut à gauche, puis ligne par ligne). Les planches
 *  suivantes repartent de la case 1. */
export function positionsPlanche(n: number, g: GrillePlanche, caseDepart = 1): { pages: CasePlanche[][]; erreur: string } {
  const parPage = g.lignes * g.colonnes;
  if (!Number.isInteger(parPage) || parPage < 1) return { pages: [], erreur: 'Gabarit de planche invalide.' };
  if (!Number.isInteger(caseDepart) || caseDepart < 1 || caseDepart > parPage) {
    return { pages: [], erreur: `La case de départ doit être entre 1 et ${parPage}.` };
  }
  const pages: CasePlanche[][] = [];
  let page: CasePlanche[] = [];
  let numero = caseDepart;
  for (let i = 0; i < n; i += 1) {
    if (numero > parPage) { pages.push(page); page = []; numero = 1; }
    const ligne = Math.floor((numero - 1) / g.colonnes) + 1;
    const colonne = ((numero - 1) % g.colonnes) + 1;
    page.push({
      etiquette: i, numeroCase: numero, ligne, colonne,
      xMm: Math.round((g.margeGaucheMm + (colonne - 1) * g.pasHorizontalMm) * 100) / 100,
      yMm: Math.round((g.margeHautMm + (ligne - 1) * g.pasVerticalMm) * 100) / 100,
    });
    numero += 1;
  }
  if (page.length) pages.push(page);
  return { pages, erreur: '' };
}

/** La table des 107 motifs Code 128 (largeurs barre/espace alternées). */
export const MOTIFS_128 = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
] as const;

/** Les valeurs de symboles d'un texte, départ et clé comprises, arrêt final.
 *  Chiffres en nombre pair → jeu C (deux chiffres par symbole, le plus dense) ;
 *  sinon jeu B. */
export function symboles128(texte: string): number[] {
  const out: number[] = [];
  if (/^(\d\d)+$/.test(texte)) {
    out.push(105);
    for (let i = 0; i < texte.length; i += 2) out.push(Number(texte.slice(i, i + 2)));
  } else {
    out.push(104);
    for (const c of texte) {
      const v = c.charCodeAt(0) - 32;
      if (v < 0 || v > 94) throw new Error(`Caractère non codable en Code 128 B : « ${c} »`);
      out.push(v);
    }
  }
  const cle = out.reduce((s, v, i) => s + v * (i === 0 ? 1 : i), 0) % 103;
  out.push(cle, 106);
  return out;
}

/** Les largeurs successives (en modules), barre d'abord, prêtes à tracer. */
export function barres128(texte: string): number[] {
  return symboles128(texte).flatMap((v) => Array.from(MOTIFS_128[v] ?? '').map(Number));
}
